import React, { useState } from 'react';
import { useCRM } from '../context/CRMContext';
import { 
    BarChart3, TrendingUp, Users, DollarSign, Target, 
    PieChart, Filter, Download, PauseCircle, PlayCircle, Edit2,
    Plus, X, Save, Globe, Smartphone, Image, MapPin, Link as LinkIcon, 
    MousePointerClick, Calendar, Bot, Search, FileText, Sparkles, Megaphone,
    ShieldCheck, Clock, FileImage, Layers, LayoutGrid
} from 'lucide-react';

// Local Mock Data for Marketing
const MARKETING_DATA = {
    funnel: [
        { stage: 'Impressions', count: 125000, dropoff: 0, color: 'bg-blue-900' },
        { stage: 'Clicks', count: 4500, dropoff: '3.6% CTR', color: 'bg-blue-700' },
        { stage: 'Registrations', count: 850, dropoff: '18% Conv', color: 'bg-blue-600' },
        { stage: 'KYC Submitted', count: 620, dropoff: '72% Drop', color: 'bg-blue-500' },
        { stage: 'Funded Accounts', count: 185, dropoff: '30% Conv', color: 'bg-green-500' }
    ],
    channels: [
        { name: 'Google Search', spend: 12000, revenue: 45000, roas: '3.75x' },
        { name: 'Meta / Instagram', spend: 5000, revenue: 12000, roas: '2.4x' },
        { name: 'Affiliate Network', spend: 8000, revenue: 52000, roas: '6.5x' },
        { name: 'Email Retargeting', spend: 1500, revenue: 8500, roas: '5.6x' },
    ],
    campaigns: [
        { id: 1, platform: 'Meta', name: 'Crypto Winter Promo', status: 'Active', complianceStatus: 'Approved', budget: '$500', objective: 'LEADS', leads: 450, cpa: '$58', roi: '420%' },
        { id: 2, platform: 'Google', name: 'Gold Spread Zero', status: 'Paused', complianceStatus: 'Pending', budget: '$200', objective: 'TRAFFIC', leads: 120, cpa: '$133', roi: '210%' },
        { id: 3, platform: 'Meta', name: 'EU Tier 1 Targeting', status: 'Active', complianceStatus: 'Approved', budget: '$1000', objective: 'CONVERSIONS', leads: 85, cpa: '$250', roi: '180%' },
        { id: 4, platform: 'TikTok', name: 'Churn Reactivation', status: 'Draft', complianceStatus: 'Rejected', budget: '$150', objective: 'APP_INSTALL', leads: 0, cpa: '$0', roi: '0%' },
    ],
    seo: {
        aeoScore: 78, // 0-100 Visibility in LLMs
        organicTraffic: '45.2k',
        domainAuthority: 62,
        keywords: [
            { term: 'Best Crypto Broker UK', googleRank: 3, aiVisibility: 'High', volume: '12k', sentiment: 'Positive' },
            { term: 'Zero Spread Gold Trading', googleRank: 1, aiVisibility: 'Medium', volume: '5.5k', sentiment: 'Neutral' },
            { term: 'Forex Trading Platform', googleRank: 8, aiVisibility: 'Low', volume: '45k', sentiment: 'Mixed' },
            { term: 'Is Nommia Safe?', googleRank: 1, aiVisibility: 'High', volume: '1.2k', sentiment: 'Positive' },
        ],
        aiSources: [
            { name: 'ChatGPT / OpenAI', share: '45%', status: 'Primary Source' },
            { name: 'Perplexity AI', share: '30%', status: 'Strong Presence' },
            { name: 'Google Gemini', share: '15%', status: 'Growing' },
            { name: 'Claude', share: '10%', status: 'Low Visibility' },
        ]
    },
    cohorts: [
        { month: 'Oct 2024', users: 150, m0: '$45k', m1: '$12k', m2: '$8k', m3: '$5k', ltv: '$466' },
        { month: 'Nov 2024', users: 180, m0: '$52k', m1: '$15k', m2: '$9k', m3: '-', ltv: '$422' },
        { month: 'Dec 2024', users: 210, m0: '$68k', m1: '$18k', m2: '-', m3: '-', ltv: '$409' },
        { month: 'Jan 2025', users: 195, m0: '$61k', m1: '-', m2: '-', m3: '-', ltv: '$312' },
    ],
    assets: [
        { id: 1, name: 'Bitcoin Gold Banner', type: 'Image', size: '1080x1080', usedIn: 4, date: '2025-01-10' },
        { id: 2, name: 'Zero Spread Video', type: 'Video', size: '1920x1080', usedIn: 2, date: '2025-01-12' },
        { id: 3, name: 'Risk Warning Footer', type: 'Text', size: 'N/A', usedIn: 15, date: '2024-11-05' },
    ]
};

const MarketingPage = () => {
const { campaigns, setCampaigns, addCampaign, updateCampaignStatus } = useCRM();
  const [activeTab, setActiveTab] = useState('paid'); 
  const [timeRange, setTimeRange] = useState('30d');
  
  // Modal State
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null); 
  
  // API-Ready Form Data Structure
  const [formData, setFormData] = useState({
      name: '',
      platform: 'Meta',
      objective: 'OUTCOME_LEADS',
      budget: '',
      startDate: '',
      endDate: '',
      locations: '',
      ageRange: '18-65',
      headline: '',
      adText: '',
      destinationUrl: '',
      callToAction: 'LEARN_MORE',
      status: 'Active',
      complianceStatus: 'Pending' // Default for FinProm
  });

  const toggleCampaign = (id) => {
      setCampaigns(campaigns.map(c => 
          c.id === id ? { ...c, status: c.status === 'Active' ? 'Paused' : 'Active' } : c
      ));
  };

  const handleOpenAdd = () => {
      setEditingId(null);
      const today = new Date().toISOString().split('T')[0];
      setFormData({ 
          name: '', platform: 'Meta', objective: 'OUTCOME_LEADS', 
          budget: '', startDate: today, endDate: '',
          locations: '', ageRange: '18-65', 
          headline: '', adText: '', destinationUrl: '', callToAction: 'LEARN_MORE',
          status: 'Draft', complianceStatus: 'Pending'
      });
      setShowModal(true);
  };

  const handleOpenEdit = (campaign) => {
      setEditingId(campaign.id);
      const today = new Date().toISOString().split('T')[0];
      setFormData({ 
          name: campaign.name, 
          platform: campaign.platform || 'Meta',
          objective: campaign.objective || 'LEADS',
          budget: campaign.budget.replace('$', '').replace('/day', ''), 
          startDate: today,
          endDate: '',
          locations: 'Global', 
          ageRange: '18-65',
          headline: 'Trade with 0% Commission', 
          adText: 'Join thousands of traders on the most advanced platform.',
          destinationUrl: 'https://nommia.com/register?promo=winter',
          callToAction: 'SIGN_UP',
          status: campaign.status,
          complianceStatus: campaign.complianceStatus
      });
      setShowModal(true);
  };

  const handleSave = () => {
      if (!formData.name || !formData.budget || !formData.startDate) return alert('Name, Budget, and Start Date required.');

      const apiPayload = {
          name: formData.name,
          platform: formData.platform,
          special_ad_categories: [], 
          objective: formData.objective,
          daily_budget: parseInt(formData.budget) * 100,
          schedule: {
              start_time: formData.startDate,
              end_time: formData.endDate || 'ONGOING'
          },
          targeting: {
              geo_locations: { countries: formData.locations.split(',') },
              age_min: parseInt(formData.ageRange.split('-')[0]),
              age_max: parseInt(formData.ageRange.split('-')[1])
          },
          creative: {
              title: formData.headline,
              body: formData.adText,
              object_url: formData.destinationUrl,
              call_to_action_type: formData.callToAction
          },
          // FinProm: Force status to paused until approved
          status: 'PAUSED',
          compliance_check: 'REQUESTED'
      };

// console.log("Sending to API:", apiPayload); 

      if (editingId) {
          // specific update logic for edit mode can be added to context later
          updateCampaignStatus(editingId, formData.status, formData.complianceStatus);
     } else {
          const newCampaign = {
              // Basic Info
              ...formData,
              budget: `$${formData.budget}`,
              leads: 0,
              cpa: '$0',
              roi: '0%',
              complianceStatus: 'Pending',
              
              // Metadata required by Compliance Page
              submittedBy: 'Current User',
              submittedDate: 'Just Now',
              issues: [], // Empty array so .map doesn't crash
              
              // The Critical Nested Structure
              creative: {
                  headline: formData.headline,
                  body: formData.adText,
                  disclaimer: 'Risk Warning: Capital at risk.'
              }
          };
          addCampaign(newCampaign); 
      }
      setShowModal(false);
  };

  return (
    <div className="p-4 md:p-8 animate-fade-in relative">
      
      {/* CAMPAIGN MODAL */}
      {showModal && (
          <div className="absolute inset-0 bg-slate-950/80 z-50 flex items-center justify-center p-4">
              <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-2xl shadow-2xl p-6 animate-fade-in flex flex-col max-h-[90vh]">
                  <div className="flex justify-between items-center mb-6 border-b border-slate-800 pb-4">
                      <div>
                        <h3 className="text-xl font-bold text-white">{editingId ? 'Edit Campaign Configuration' : 'Launch New Campaign'}</h3>
                        <p className="text-slate-400 text-xs mt-1">Configures Ad Set & Creative via Marketing API</p>
                      </div>
                      <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-white"><X size={20}/></button>
                  </div>
                  
                  <div className="space-y-6 overflow-y-auto pr-2 flex-1">
                    <div>
                          <label className="text-xs font-bold text-slate-400 uppercase block mb-2">Campaign Name</label>
                          <input 
                              className="w-full bg-slate-950 border border-slate-700 rounded-lg p-3 text-white focus:ring-2 focus:ring-blue-500 outline-none"
                              placeholder="e.g. Summer Crypto Promo"
                              value={formData.name}
                              onChange={(e) => setFormData({...formData, name: e.target.value})}
                          />
                      </div>
                      {/* Section 1: Platform & Objective */}
                      <div className="grid grid-cols-2 gap-4">
                          <div>
                              <label className="text-xs font-bold text-slate-400 uppercase block mb-2">Ad Platform</label>
                              <div className="flex gap-2">
                                  {['Meta', 'Google', 'TikTok'].map(p => (
                                      <button 
                                        key={p} 
                                        onClick={() => setFormData({...formData, platform: p})}
                                        className={`flex-1 py-2 rounded-lg text-xs font-bold border transition-colors ${formData.platform === p ? 'bg-blue-600 border-blue-500 text-white' : 'bg-slate-950 border-slate-700 text-slate-400'}`}
                                      >
                                          {p}
                                      </button>
                                  ))}
                              </div>
                          </div>
                          <div>
                              <label className="text-xs font-bold text-slate-400 uppercase block mb-2">Campaign Objective</label>
                              <select 
                                  className="w-full bg-slate-800 border border-slate-600 rounded-lg p-2 text-white text-sm"
                                  value={formData.objective}
                                  onChange={(e) => setFormData({...formData, objective: e.target.value})}
                              >
                                  <option value="OUTCOME_LEADS">Leads (Form Fills)</option>
                                  <option value="OUTCOME_TRAFFIC">Traffic (Link Clicks)</option>
                                  <option value="OUTCOME_SALES">Conversions (Deposits)</option>
                                  <option value="BRAND_AWARENESS">Brand Awareness</option>
                              </select>
                          </div>
                      </div>

                      {/* Section 2: Budget & Schedule */}
                      <div className="bg-slate-950 p-4 rounded-xl border border-slate-800">
                          <h4 className="text-xs font-bold text-green-400 uppercase mb-3 flex items-center gap-2"><DollarSign size={14}/> Budget & Schedule</h4>
                          <div className="grid grid-cols-3 gap-4">
                              <div className="col-span-1">
                                  <label className="text-[10px] text-slate-500 uppercase block mb-1">Daily Spend (USD)</label>
                                  <input 
                                      type="number"
                                      className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2 text-white text-sm"
                                      placeholder="500"
                                      value={formData.budget}
                                      onChange={(e) => setFormData({...formData, budget: e.target.value})}
                                  />
                              </div>
                              <div className="col-span-1">
                                  <label className="text-[10px] text-slate-500 uppercase block mb-1">Start Date</label>
                                  <input 
                                      type="date"
                                      className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2 text-white text-sm"
                                      value={formData.startDate}
                                      onChange={(e) => setFormData({...formData, startDate: e.target.value})}
                                  />
                              </div>
                              <div className="col-span-1">
                                  <label className="text-[10px] text-slate-500 uppercase block mb-1">End Date</label>
                                  <input 
                                      type="date"
                                      className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2 text-white text-sm"
                                      value={formData.endDate}
                                      onChange={(e) => setFormData({...formData, endDate: e.target.value})}
                                  />
                              </div>
                          </div>
                      </div>

                      {/* Section 3: Targeting (Ad Set Level) */}
                      <div className="bg-slate-950 p-4 rounded-xl border border-slate-800">
                          <h4 className="text-xs font-bold text-blue-400 uppercase mb-3 flex items-center gap-2"><Target size={14}/> Targeting</h4>
                          <div className="grid grid-cols-2 gap-4">
                              <div>
                                  <label className="text-[10px] text-slate-500 uppercase block mb-1">Locations</label>
                                  <input 
                                      className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2 text-white text-sm"
                                      placeholder="US, GB..."
                                      value={formData.locations}
                                      onChange={(e) => setFormData({...formData, locations: e.target.value})}
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] text-slate-500 uppercase block mb-1">Age Range</label>
                                  <input 
                                      className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2 text-white text-sm"
                                      value={formData.ageRange}
                                      onChange={(e) => setFormData({...formData, ageRange: e.target.value})}
                                  />
                              </div>
                          </div>
                      </div>

                      {/* Section 4: Creative (Ad Level) */}
                      <div className="bg-slate-950 p-4 rounded-xl border border-slate-800">
                          <h4 className="text-xs font-bold text-purple-400 uppercase mb-3 flex items-center gap-2"><Image size={14}/> Ad Content & Creative</h4>
                          <div className="space-y-4">
                              <div>
                                  <label className="text-[10px] text-slate-500 uppercase block mb-1">Headline</label>
                                  <input 
                                      className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2 text-white text-sm"
                                      value={formData.headline}
                                      onChange={(e) => setFormData({...formData, headline: e.target.value})}
                                  />
                              </div>
                              <div>
                                  <label className="text-[10px] text-slate-500 uppercase block mb-1">Text</label>
                                  <textarea 
                                      className="w-full bg-slate-900 border border-slate-700 rounded-lg p-2 text-white text-sm"
                                      value={formData.adText}
                                      onChange={(e) => setFormData({...formData, adText: e.target.value})}
                                  />
                              </div>
                              <div className="grid grid-cols-2 gap-4">
                                  <div>
                                      <label className="text-[10px] text-slate-500 uppercase block mb-1">Destination URL</label>
                                      <div className="flex items-center bg-slate-900 border border-slate-700 rounded-lg px-2">
                                          <LinkIcon size={14} className="text-slate-500 mr-2"/>
                                          <input 
                                              className="bg-transparent w-full py-2 text-white text-sm outline-none"
                                              placeholder="https://..."
                                              value={formData.destinationUrl}
                                              onChange={(e) => setFormData({...formData, destinationUrl: e.target.value})}
                                          />
                                      </div>
                                  </div>
                                  <div>
                                      <label className="text-[10px] text-slate-500 uppercase block mb-1">Call To Action</label>
                                      <div className="flex items-center bg-slate-900 border border-slate-700 rounded-lg px-2">
                                          <MousePointerClick size={14} className="text-slate-500 mr-2"/>
                                          <select 
                                              className="bg-transparent w-full py-2 text-white text-sm outline-none"
                                              value={formData.callToAction}
                                              onChange={(e) => setFormData({...formData, callToAction: e.target.value})}
                                          >
                                              <option value="LEARN_MORE">Learn More</option>
                                              <option value="SIGN_UP">Sign Up</option>
                                              <option value="GET_OFFER">Get Offer</option>
                                              <option value="APPLY_NOW">Apply Now</option>
                                          </select>
                                      </div>
                                  </div>
                              </div>
                              <div>
                                  <label className="text-[10px] text-slate-500 uppercase block mb-1">Media (Image/Video)</label>
                                  <div className="border-2 border-dashed border-slate-700 rounded-lg p-6 text-center hover:bg-slate-900 transition-colors cursor-pointer group">
                                      <Image className="mx-auto text-slate-500 mb-2 group-hover:text-white" />
                                      <p className="text-xs text-slate-400 group-hover:text-white">Click to upload 1080x1080px Image or Video</p>
                                  </div>
                              </div>
                          </div>
                      </div>
                  </div>
                  
                  {/* FinProm Warning */}
                  <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg mb-4 flex items-center gap-3">
                      <ShieldCheck className="text-amber-500 flex-shrink-0" size={16} />
                      <p className="text-[10px] text-amber-200">
                          <strong>FinProm Compliance:</strong> Launching this campaign will submit it for compliance review. It will not go live until approved by an officer.
                      </p>
                  </div>

                  <div className="pt-4 mt-4 border-t border-slate-800 flex gap-3">
                      <button onClick={() => setShowModal(false)} className="flex-1 py-3 bg-slate-800 text-slate-300 font-bold rounded-lg hover:bg-slate-700 transition-colors">Cancel</button>
                      <button onClick={handleSave} className="flex-1 py-3 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-500 transition-colors flex items-center justify-center gap-2"><Save size={18} /> {editingId ? 'Sync Updates' : 'Submit for Approval'}</button>
                  </div>
              </div>
          </div>
      )}

      {/* HEADER & TABS */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
         <div>
            <h2 className="text-2xl md:text-3xl font-bold text-white tracking-tight">Marketing Intelligence</h2>
            <p className="text-slate-400 mt-1">Campaign ROI, Lead Funnels & Attribution.</p>
         </div>
         <div className="flex items-center gap-4">
            <div className="bg-slate-900 p-1 rounded-lg border border-slate-800 flex overflow-x-auto">
                <button 
                    onClick={() => setActiveTab('paid')} 
                    className={`px-4 py-2 rounded-md text-sm font-bold transition-all flex items-center gap-2 whitespace-nowrap ${activeTab === 'paid' ? 'bg-slate-800 text-white shadow-sm' : 'text-slate-500 hover:text-slate-300'}`}
                >
                    <Megaphone size={14} /> Paid Media
                </button>
                <button 
                    onClick={() => setActiveTab('organic')} 
                    className={`px-4 py-2 rounded-md text-sm font-bold transition-all flex items-center gap-2 whitespace-nowrap ${activeTab === 'organic' ? 'bg-slate-800 text-white shadow-sm' : 'text-slate-500 hover:text-slate-300'}`}
                >
                    <Bot size={14} /> Organic & AEO
                </button>
                <button 
                    onClick={() => setActiveTab('retention')} 
                    className={`px-4 py-2 rounded-md text-sm font-bold transition-all flex items-center gap-2 whitespace-nowrap ${activeTab === 'retention' ? 'bg-slate-800 text-white shadow-sm' : 'text-slate-500 hover:text-slate-300'}`}
                >
                    <Users size={14} /> Retention & LTV
                </button>
                <button 
                    onClick={() => setActiveTab('assets')} 
                    className={`px-4 py-2 rounded-md text-sm font-bold transition-all flex items-center gap-2 whitespace-nowrap ${activeTab === 'assets' ? 'bg-slate-800 text-white shadow-sm' : 'text-slate-500 hover:text-slate-300'}`}
                >
                    <FileImage size={14} /> Creative Assets
                </button>
            </div>
            
            <button className="bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2">
                <Download size={16} /> Report
            </button>
         </div>
      </div>

      {/* --- PAID MEDIA TAB --- */}
      {activeTab === 'paid' && (
          <>
            {/* Top Level KPIs */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
                <div className="bg-slate-900 p-6 rounded-2xl border border-slate-800">
                    <p className="text-slate-500 text-xs font-bold uppercase flex items-center gap-2"><DollarSign size={14}/> Total Spend</p>
                    <p className="text-2xl font-bold text-white mt-2">$26,500</p>
                    <span className="text-xs text-red-400 font-bold mt-1 block">+12% vs last month</span>
                </div>
                <div className="bg-slate-900 p-6 rounded-2xl border border-slate-800">
                    <p className="text-slate-500 text-xs font-bold uppercase flex items-center gap-2"><Users size={14}/> Net FTDs</p>
                    <p className="text-2xl font-bold text-green-400 mt-2">185</p>
                    <span className="text-xs text-green-500 font-bold mt-1 block">+8% vs last month</span>
                </div>
                <div className="bg-slate-900 p-6 rounded-2xl border border-slate-800">
                    <p className="text-slate-500 text-xs font-bold uppercase flex items-center gap-2"><Target size={14}/> Avg CPA</p>
                    <p className="text-2xl font-bold text-blue-400 mt-2">$143</p>
                    <span className="text-xs text-green-500 font-bold mt-1 block">-5% (Improved)</span>
                </div>
                <div className="bg-slate-900 p-6 rounded-2xl border border-slate-800">
                    <p className="text-slate-500 text-xs font-bold uppercase flex items-center gap-2"><TrendingUp size={14}/> Global ROI</p>
                    <p className="text-2xl font-bold text-amber-500 mt-2">440%</p>
                    <span className="text-xs text-slate-500 font-bold mt-1 block">Est. Revenue: $116k</span>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
                {/* Conversion Funnel */}
                <div className="lg:col-span-2 bg-slate-900 rounded-2xl border border-slate-800 p-6">
                    <h3 className="font-bold text-white mb-6 flex items-center gap-2"><Filter size={18} className="text-blue-500"/> Conversion Funnel</h3>
                    <div className="space-y-4">
                        {MARKETING_DATA.funnel.map((step, i) => (
                            <div key={i} className="relative">
                                <div className="flex justify-between text-sm mb-1 z-10 relative">
                                    <span className="font-bold text-white">{step.stage}</span>
                                    <div className="text-right">
                                        <span className="font-mono text-white block">{step.count.toLocaleString()}</span>
                                        <span className="text-[10px] text-slate-500">{step.dropoff}</span>
                                    </div>
                                </div>
                                <div className="h-8 w-full bg-slate-950 rounded-lg overflow-hidden relative">
                                    <div className={`h-full ${step.color} opacity-80 transition-all duration-1000`} style={{ width: `${(step.count / MARKETING_DATA.funnel[0].count) * 100}%` }}></div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Channel Performance */}
                <div className="bg-slate-900 rounded-2xl border border-slate-800 p-6">
                    <h3 className="font-bold text-white mb-6 flex items-center gap-2"><PieChart size={18} className="text-purple-500"/> Channel Efficiency</h3>
                    <div className="space-y-5">
                        {MARKETING_DATA.channels.map((chan, i) => (
                            <div key={i} className="group">
                                <div className="flex justify-between items-center mb-1">
                                    <span className="text-sm text-slate-300">{chan.name}</span>
                                    <span className="text-xs font-bold text-green-400 bg-green-900/20 px-2 py-0.5 rounded">{chan.roas} ROAS</span>
                                </div>
                                <div className="w-full bg-slate-950 h-1.5 rounded-full overflow-hidden mb-1">
                                    <div className="bg-purple-500 h-full" style={{ width: `${(chan.revenue / 52000) * 100}%` }}></div>
                                </div>
                                <div className="flex justify-between text-[10px] text-slate-500">
                                    <span>Spend: ${chan.spend.toLocaleString()}</span>
                                    <span>Rev: ${chan.revenue.toLocaleString()}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Campaign Management Table */}
            <div className="bg-slate-900 rounded-2xl border border-slate-800 overflow-hidden">
                <div className="p-6 border-b border-slate-800 flex justify-between items-center">
                    <h3 className="font-bold text-white">Active Campaigns</h3>
                    <button onClick={handleOpenAdd} className="text-xs bg-slate-800 hover:bg-slate-700 text-white px-3 py-1.5 rounded transition-colors flex items-center gap-2">
                        <Plus size={14} /> New Campaign
                    </button>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead className="bg-slate-950 text-xs font-bold text-slate-400 uppercase">
                            <tr>
                                <th className="px-6 py-4">Campaign Name</th>
                                <th className="px-6 py-4">Platform</th>
                                <th className="px-6 py-4">Compliance Status</th>
                                <th className="px-6 py-4">Daily Budget</th>
                                <th className="px-6 py-4 text-center">Leads</th>
                                <th className="px-6 py-4 text-center">CPA</th>
                                <th className="px-6 py-4 text-right">ROI</th>
                                <th className="px-6 py-4 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-800">
                            {campaigns.map((c) => (
                                <tr key={c.id} className="hover:bg-slate-800/50 transition-colors">
                                    <td className="px-6 py-4 font-bold text-white">{c.name}</td>
                                    <td className="px-6 py-4"><div className="flex items-center gap-2 text-slate-300">{c.platform === 'Google' ? <Globe size={14}/> : c.platform === 'TikTok' ? <Smartphone size={14}/> : <Users size={14}/>}{c.platform || 'Meta'}</div></td>
                                    <td className="px-6 py-4">
                                        <span className={`px-2 py-1 rounded text-[10px] font-bold uppercase border ${
                                            c.complianceStatus === 'Approved' ? 'bg-green-500/10 text-green-400 border-green-500/20' : 
                                            c.complianceStatus === 'Rejected' ? 'bg-red-500/10 text-red-400 border-red-500/20' : 
                                            'bg-amber-500/10 text-amber-400 border-amber-500/20'
                                        }`}>
                                            {c.complianceStatus}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-slate-300 font-mono text-sm">{c.budget}</td>
                                    <td className="px-6 py-4 text-center text-white font-bold">{c.leads}</td>
                                    <td className="px-6 py-4 text-center text-slate-400">{c.cpa}</td>
                                    <td className="px-6 py-4 text-right text-green-400 font-bold">{c.roi}</td>
                                    <td className="px-6 py-4 text-right flex justify-end gap-2">
                                        <button onClick={() => toggleCampaign(c.id)} className={`p-1.5 rounded hover:bg-slate-700 transition-colors ${c.status === 'Active' ? 'text-amber-500' : 'text-green-500'}`}>{c.status === 'Active' ? <PauseCircle size={16} /> : <PlayCircle size={16} />}</button>
                                        <button onClick={() => handleOpenEdit(c)} className="p-1.5 rounded text-slate-400 hover:text-white hover:bg-slate-700 transition-colors"><Edit2 size={16} /></button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
          </>
      )}

      {/* --- ORGANIC & AEO TAB --- */}
      {activeTab === 'organic' && (
          <div className="space-y-8 animate-fade-in">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {/* AEO Intelligence */}
                  <div className="bg-slate-900 p-6 rounded-2xl border border-slate-800">
                      <h3 className="font-bold text-white mb-6 flex items-center gap-2"><Bot size={20} className="text-purple-500"/> AEO & LLM Visibility</h3>
                      <div className="grid grid-cols-2 gap-6 mb-6">
                          <div className="text-center p-4 bg-slate-950 rounded-xl">
                              <span className="text-xs text-slate-500 uppercase">Citation Score</span>
                              <p className="text-3xl font-bold text-purple-400 mt-1">{MARKETING_DATA.seo.aeoScore}/100</p>
                          </div>
                          <div className="text-center p-4 bg-slate-950 rounded-xl">
                              <span className="text-xs text-slate-500 uppercase">Est. Organic Traffic</span>
                              <p className="text-3xl font-bold text-white mt-1">{MARKETING_DATA.seo.organicTraffic}</p>
                          </div>
                      </div>
                      <div className="space-y-4">
                          <h4 className="text-xs font-bold text-slate-500 uppercase">Share of Voice (AI Models)</h4>
                          {MARKETING_DATA.seo.aiSources.map((source, i) => (
                              <div key={i} className="flex items-center justify-between">
                                  <span className="text-sm text-slate-300">{source.name}</span>
                                  <div className="flex items-center gap-3 w-1/2">
                                      <div className="h-1.5 flex-1 bg-slate-950 rounded-full overflow-hidden">
                                          <div className="h-full bg-purple-600" style={{width: source.share}}></div>
                                      </div>
                                      <span className="text-xs font-bold text-white w-8 text-right">{source.share}</span>
                                  </div>
                              </div>
                          ))}
                      </div>
                  </div>

                  {/* Content Optimizer */}
                  <div className="bg-slate-900 p-6 rounded-2xl border border-slate-800">
                      <h3 className="font-bold text-white mb-6 flex items-center gap-2"><FileText size={20} className="text-blue-500"/> Content Optimizer</h3>
                      <div className="p-6 bg-slate-950 rounded-xl border border-dashed border-slate-800 text-center mb-6">
                          <Sparkles className="mx-auto text-slate-500 mb-2" />
                          <p className="text-slate-400 text-sm">Paste content URL or text to analyze for AI Search readiness.</p>
                      </div>
                      <div className="space-y-3">
                          <div className="flex justify-between p-3 bg-slate-950 rounded-lg border border-slate-800">
                              <span className="text-sm text-slate-300">Entity Density Analysis</span>
                              <span className="text-xs font-bold text-green-400">Good</span>
                          </div>
                          <div className="flex justify-between p-3 bg-slate-950 rounded-lg border border-slate-800">
                              <span className="text-sm text-slate-300">Structured Data (Schema)</span>
                              <span className="text-xs font-bold text-amber-500">Missing</span>
                          </div>
                          <div className="flex justify-between p-3 bg-slate-950 rounded-lg border border-slate-800">
                              <span className="text-sm text-slate-300">Sentiment Alignment</span>
                              <span className="text-xs font-bold text-green-400">Positive</span>
                          </div>
                      </div>
                  </div>
              </div>

              {/* Keyword Tracking Table */}
              <div className="bg-slate-900 rounded-2xl border border-slate-800 overflow-hidden">
                  <div className="p-6 border-b border-slate-800"><h3 className="font-bold text-white flex items-center gap-2"><Search size={18} className="text-slate-400"/> Keyword Intelligence</h3></div>
                  <table className="w-full text-left">
                      <thead className="bg-slate-950 text-xs font-bold text-slate-400 uppercase">
                          <tr>
                              <th className="px-6 py-4">Keyword / Topic</th>
                              <th className="px-6 py-4">Google Rank</th>
                              <th className="px-6 py-4">AI Visibility</th>
                              <th className="px-6 py-4">Search Vol</th>
                              <th className="px-6 py-4 text-right">Sentiment</th>
                          </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-800">
                          {MARKETING_DATA.seo.keywords.map((kw, i) => (
                              <tr key={i} className="hover:bg-slate-800/50 transition-colors">
                                  <td className="px-6 py-4 font-bold text-white">{kw.term}</td>
                                  <td className="px-6 py-4"><span className="font-mono text-blue-400">#{kw.googleRank}</span></td>
                                  <td className="px-6 py-4">
                                      <span className={`px-2 py-1 rounded text-[10px] font-bold uppercase ${kw.aiVisibility === 'High' ? 'bg-green-500/10 text-green-400' : kw.aiVisibility === 'Medium' ? 'bg-amber-500/10 text-amber-500' : 'bg-red-500/10 text-red-500'}`}>
                                          {kw.aiVisibility}
                                      </span>
                                  </td>
                                  <td className="px-6 py-4 text-slate-400 font-mono text-sm">{kw.volume}</td>
                                  <td className="px-6 py-4 text-right text-sm text-slate-300">{kw.sentiment}</td>
                              </tr>
                          ))}
                      </tbody>
                  </table>
              </div>
          </div>
      )}

      {/* --- RETENTION & LTV TAB (NEW) --- */}
      {activeTab === 'retention' && (
          <div className="space-y-6 animate-fade-in">
              <div className="bg-slate-900 rounded-2xl border border-slate-800 p-6">
                  <h3 className="font-bold text-white mb-6 flex items-center gap-2"><Layers size={20} className="text-amber-500"/> Cohort Analysis (Deposit Retention)</h3>
                  <div className="overflow-x-auto">
                      <table className="w-full text-left border-collapse">
                          <thead className="bg-slate-950 text-xs font-bold text-slate-500 uppercase">
                              <tr>
                                  <th className="px-6 py-4 border-b border-slate-800">Acquisition Month</th>
                                  <th className="px-6 py-4 border-b border-slate-800 text-center">New Users</th>
                                  <th className="px-6 py-4 border-b border-slate-800 text-right bg-blue-900/10">Month 0</th>
                                  <th className="px-6 py-4 border-b border-slate-800 text-right bg-blue-900/10">Month 1</th>
                                  <th className="px-6 py-4 border-b border-slate-800 text-right bg-blue-900/10">Month 2</th>
                                  <th className="px-6 py-4 border-b border-slate-800 text-right bg-blue-900/10">Month 3</th>
                                  <th className="px-6 py-4 border-b border-slate-800 text-right text-green-400">Est. LTV</th>
                              </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-800 text-sm">
                              {MARKETING_DATA.cohorts.map((row, i) => (
                                  <tr key={i} className="hover:bg-slate-800/30">
                                      <td className="px-6 py-4 font-bold text-white">{row.month}</td>
                                      <td className="px-6 py-4 text-center text-slate-300">{row.users}</td>
                                      <td className="px-6 py-4 text-right font-mono bg-blue-500/5">{row.m0}</td>
                                      <td className="px-6 py-4 text-right font-mono bg-blue-500/5">{row.m1}</td>
                                      <td className="px-6 py-4 text-right font-mono bg-blue-500/5">{row.m2}</td>
                                      <td className="px-6 py-4 text-right font-mono bg-blue-500/5">{row.m3}</td>
                                      <td className="px-6 py-4 text-right font-bold text-green-400">{row.ltv}</td>
                                  </tr>
                              ))}
                          </tbody>
                      </table>
                  </div>
              </div>
          </div>
      )}

      {/* --- CREATIVE ASSETS TAB (NEW) --- */}
      {activeTab === 'assets' && (
          <div className="space-y-6 animate-fade-in">
              <div className="flex justify-between items-center mb-4">
                   <h3 className="text-xl font-bold text-white flex items-center gap-2"><LayoutGrid size={20} className="text-slate-400" /> Media Library</h3>
                   <button className="bg-slate-800 hover:bg-slate-700 text-white px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2"><Plus size={16}/> Upload Asset</button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-6">
                  {MARKETING_DATA.assets.map(asset => (
                      <div key={asset.id} className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden group cursor-pointer hover:border-slate-600 transition-all">
                          <div className="aspect-square bg-slate-950 flex items-center justify-center relative">
                              <Image size={32} className="text-slate-700" />
                              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                                  <button className="text-white text-xs font-bold border border-white px-3 py-1 rounded-full">View</button>
                              </div>
                          </div>
                          <div className="p-3">
                              <p className="text-sm font-bold text-white truncate">{asset.name}</p>
                              <p className="text-[10px] text-slate-500 mt-1">{asset.type} • {asset.size}</p>
                              <div className="flex items-center gap-1 mt-2 text-[10px] text-blue-400 bg-blue-900/20 px-2 py-1 rounded w-fit">
                                  <Megaphone size={10} /> Used in {asset.usedIn} Ads
                              </div>
                          </div>
                      </div>
                  ))}
                  {/* Upload Placeholder */}
                  <div className="border-2 border-dashed border-slate-800 rounded-xl flex flex-col items-center justify-center text-slate-500 hover:text-white hover:border-slate-600 transition-all cursor-pointer min-h-[200px]">
                      <Plus size={32} className="mb-2" />
                      <span className="text-xs font-bold uppercase">Upload New</span>
                  </div>
              </div>
          </div>
      )}
    </div>
  );
};

export default MarketingPage;